import { ChildProfile } from '../types/profile';

export interface StalenessThreshold {
  value: string;
  label: string;
  days: number;
}

export const STALENESS_OPTIONS: StalenessThreshold[] = [
  { value: '1week', label: '1 Week', days: 7 },
  { value: '2weeks', label: '2 Weeks', days: 14 },
  { value: '3weeks', label: '3 Weeks', days: 21 },
  { value: '1month', label: '1 Month', days: 30 },
  { value: '2months', label: '2 Months', days: 60 },
  { value: '3months', label: '3 Months', days: 90 },
];

/**
 * Check if a domain's milestone results are stale based on the computation timestamp
 * @param computationTimestamp - ISO string when the domain results were computed (null means never computed)
 * @param stalenessThreshold - Threshold setting value (e.g., '2weeks')
 * @returns Object with staleness information
 */
export function checkDomainStaleness(
  computationTimestamp: string | null,
  stalenessThreshold: string = '2weeks'
): {
  isStale: boolean;
  daysSinceComputed: number | null;
  threshold: StalenessThreshold;
} {
  const threshold = STALENESS_OPTIONS.find(opt => opt.value === stalenessThreshold) || STALENESS_OPTIONS[1];
  
  // If never computed, it's not technically stale (it's new/uncomputed)
  if (!computationTimestamp) {
    return {
      isStale: false,
      daysSinceComputed: null,
      threshold
    };
  }

  const computedDate = new Date(computationTimestamp);
  const now = new Date();
  const diffMs = now.getTime() - computedDate.getTime();
  const daysSinceComputed = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  return {
    isStale: daysSinceComputed > threshold.days,
    daysSinceComputed,
    threshold
  };
}

/**
 * Get staleness status for all domains in a profile
 * @param profile - Child profile with domain computation timestamps
 * @param stalenessThreshold - Threshold setting value
 * @returns Object mapping domain names to staleness status
 */
export function getProfileDomainStaleness(
  profile: ChildProfile,
  stalenessThreshold: string = '2weeks'
): Record<string, ReturnType<typeof checkDomainStaleness>> {
  const domains = ['communication', 'motor', 'finemotor', 'grossmotor', 'cognitive', 'socialemotional'] as const;
  
  const staleness: Record<string, ReturnType<typeof checkDomainStaleness>> = {};
  
  for (const domain of domains) {
    const computationTimestamp = profile.devresultcomputed?.[domain] || null;
    staleness[domain] = checkDomainStaleness(computationTimestamp, stalenessThreshold);
  }
  
  return staleness;
}

/**
 * Get a user-friendly label for staleness status
 * @param staleness - Staleness check result
 * @returns Human-readable status string
 */
export function getStalenessLabel(staleness: ReturnType<typeof checkDomainStaleness>): string {
  if (staleness.daysSinceComputed === null) {
    return 'Not computed';
  }
  
  if (staleness.isStale) {
    return `Out of date (${staleness.daysSinceComputed} days ago)`;
  }
  
  if (staleness.daysSinceComputed === 0) {
    return 'Updated today';
  }
  
  if (staleness.daysSinceComputed === 1) {
    return 'Updated yesterday';
  }
  
  return `Updated ${staleness.daysSinceComputed} days ago`;
}

/**
 * Load staleness threshold setting from storage
 * @param storage - Storage interface
 * @returns Promise resolving to threshold value or default
 */
export async function loadStalenessThreshold(storage: any): Promise<string> {
  if (!storage) {
    return '2weeks'; // Default
  }
  
  try {
    const stored = await storage.getItem('milestone_staleness_threshold');
    if (stored && STALENESS_OPTIONS.find(opt => opt.value === stored)) {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to load staleness threshold:', error);
  }
  
  return '2weeks'; // Default
}

/**
 * Load concern freshness threshold setting from storage
 * @param storage - Storage interface
 * @returns Promise resolving to threshold value or default
 */
export async function loadConcernFreshnessThreshold(storage: any): Promise<string> {
  if (!storage) {
    return '2weeks'; // Default
  }
  try {
    const stored = await storage.getItem('concern_freshness_threshold');
    if (stored && STALENESS_OPTIONS.find(opt => opt.value === stored)) {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to load concern freshness threshold:', error);
  }
  return '2weeks'; // Default
}

/**
 * Load debug setting for showing last update timestamps
 * @param storage - Storage interface
 * @returns Promise resolving to boolean (defaults to enabled)
 */
export async function loadShowLastUpdateSetting(storage: any): Promise<boolean> {
  if (!storage) {
    return true; // Default to enabled
  }
  
  try {
    const stored = await storage.getItem('debug_show_last_update');
    return stored !== 'false'; // Only disabled if explicitly set to 'false'
  } catch (error) {
    console.warn('Failed to load show last update setting:', error);
  }
  
  return true; // Default to enabled
}

/**
 * Format a computation timestamp for display in locale-specific format
 * @param timestamp - ISO timestamp string
 * @returns Formatted date string
 */
export function formatComputationTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    // Show relative time for recent timestamps
    if (diffMs < 60 * 1000) {
      return 'Just now';
    } else if (diffMs < 60 * 60 * 1000) {
      const minutes = Math.floor(diffMs / (1000 * 60));
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      // Show formatted date for older timestamps
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  } catch (error) {
    console.warn('Failed to format computation timestamp:', error);
    return 'Invalid date';
  }
}